import { normalizeHex, type Color } from '@pz/scene';
import type { DesignerFont, DesignerFontCategory } from '../designer-types.js';
import { el, hexOf, iconBtn, iconSvg, sameHex, searchBox, type DesignerCtx } from './panels.js';
import { clamp, round } from './state.js';

/**
 * Controls both shells share: the colour block, the font picker and the
 * stepper. They are DOM builders over `ctx` and nothing else, so a phone
 * sub-panel and a desktop Effects section show the same picker, and the
 * behaviours that are easy to get subtly wrong — what a hex field accepts,
 * which face "Bold" switches to, how a stepper reverts a typo — exist once.
 *
 * Everything above the DOM builders is pure and tested in `controls.test.ts`;
 * that is deliberate, because the widget's tests run without a DOM and logic
 * that lives inside an event handler is logic nobody checks again.
 */

// ── Colours ──────────────────────────────────────────────────────────────────

/**
 * `#rgb`, `#rrggbb`, with or without the hash, any case — and nothing else.
 * Returns the normalised `#rrggbb` form, or undefined for anything a shopper
 * could still be in the middle of typing.
 */
export function parseHexInput(raw: string): string | undefined {
  const value = raw.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$/.test(value) && !/^[0-9a-fA-F]{6}$/.test(value)) return undefined;
  const full = value.length === 3
    ? value.split('').map((digit) => digit + digit).join('')
    : value;
  return normalizeHex(`#${full}`) || undefined;
}

/**
 * The two grids behind "Dodatne boje": a print-safe ramp and the screen
 * palette. Both are resolved by the platform (`DESIGN_PRINT_SAFE_PALETTE` /
 * `DESIGN_WEB_PALETTE`, overridable per zone through `text.printPalette` and
 * `text.webPalette`), so the designer only has to read them.
 */
export function printPalette(ctx: DesignerCtx): Color[] {
  return ctx.config.text.printPalette;
}

export function webPalette(ctx: DesignerCtx): Color[] {
  return ctx.config.text.webPalette;
}

// ── Fonts ────────────────────────────────────────────────────────────────────

export const FONT_CATEGORIES: DesignerFontCategory[] = [
  'serif', 'sans', 'script', 'handwriting', 'display', 'decorative', 'mono', 'symbols',
];

/** Tested in order: the first pattern that matches names the category. */
const CATEGORY_PATTERNS: Array<[DesignerFontCategory, RegExp]> = [
  ['mono', /\bmono|monospace|code|courier|consol|typewriter/],
  ['symbols', /symbol|dingbat|emoji|icon|wingding|ornament/],
  ['script', /script|calligra|cursive|brush|signature|pacifico|dancing|vibes|allura|sacramento|lobster/],
  ['handwriting', /hand|write|written|marker|comic|caveat|indie|shadows into|patrick|kalam|schoolbell/],
  ['decorative', /decor|fancy|stencil|distress|grunge|retro|vintage|neon|western|pixel|8-?bit|graffiti|horror|sci-?fi/],
  // Serif is tested before sans on purpose: "Roboto Slab" is a slab serif even
  // though "Roboto" is the name of a sans family.
  ['serif', /serif|slab|times|georgia|garamond|playfair|merriweather|roman|bookman|baskerville|didone|antiqua/],
  ['sans', /\bsans\b|grotesk|grotesque|helvetica|arial|\binter\b|roboto|montserrat|lato|poppins|nunito|rubik|raleway|ubuntu|round/],
  ['display', /display|poster|headline|impact|bebas|anton|oswald|\btitle\b|\bfat\b/],
];

/**
 * The picker's grouping. The shop's own `category` wins; the heuristic below
 * is for the fonts a shop imported without one — a picker where every face
 * sits under "Svi" is a picker nobody scrolls.
 */
export function fontCategoryOf(font: DesignerFont): DesignerFontCategory {
  if (font.category && FONT_CATEGORIES.includes(font.category)) return font.category;
  const name = `${font.family} ${font.style}`.toLowerCase();
  for (const [category, pattern] of CATEGORY_PATTERNS) {
    if (pattern.test(name)) return category;
  }
  return 'sans';
}

/** Faces that cover Cyrillic, for the Ćirilica chip. */
export function coversCyrillic(font: DesignerFont): boolean {
  return (font.scripts ?? []).some((script) => script.toLowerCase().startsWith('cyrillic'));
}

export interface FaceFlags {
  bold: boolean;
  italic: boolean;
}

/** What a face's style name says about it — Zazzle's own two axes. */
export function faceFlags(font: DesignerFont): FaceFlags {
  const style = font.style ?? '';
  return { bold: /bold/i.test(style), italic: /italic|oblique/i.test(style) };
}

/**
 * The face "Bold" (or "Italic") should switch to: the same family with that
 * one axis flipped and the other kept. Undefined when the family has no such
 * face — which is what disables the button, exactly as Zazzle does, rather
 * than faking a weight the print file cannot produce.
 */
export function siblingFace(
  fonts: readonly DesignerFont[],
  fontId: string,
  axis: 'bold' | 'italic',
  on: boolean,
): string | undefined {
  const current = fonts.find((font) => font.id === fontId);
  if (!current) return undefined;
  const flags = faceFlags(current);
  const wanted: FaceFlags = { ...flags, [axis]: on };
  if (wanted.bold === flags.bold && wanted.italic === flags.italic) return current.id;
  const family = current.family.toLowerCase();
  const siblings = fonts.filter((font) => font.family.toLowerCase() === family);
  const exact = siblings.find((font) => {
    const other = faceFlags(font);
    return other.bold === wanted.bold && other.italic === wanted.italic;
  });
  return exact?.id;
}

export interface FontFamilyGroup {
  family: string;
  category: DesignerFontCategory;
  cyrillic: boolean;
  /** Every face of the family; the first is the one the rows show. */
  faces: DesignerFont[];
}

/** One row per family, faces folded under it, families in Serbian order. */
export function groupFontFamilies(fonts: readonly DesignerFont[]): FontFamilyGroup[] {
  const groups = new Map<string, FontFamilyGroup>();
  for (const font of fonts) {
    const key = font.family.toLowerCase();
    const existing = groups.get(key);
    if (existing) {
      existing.faces.push(font);
      existing.cyrillic ||= coversCyrillic(font);
      continue;
    }
    groups.set(key, {
      family: font.family,
      category: fontCategoryOf(font),
      cyrillic: coversCyrillic(font),
      faces: [font],
    });
  }
  const list = [...groups.values()];
  for (const group of list) {
    // The plainest face first: that is the one whose name the row renders in.
    group.faces.sort((a, b) => weightOf(a) - weightOf(b));
  }
  list.sort((a, b) => a.family.localeCompare(b.family, 'sr-RS'));
  return list;
}

function weightOf(font: DesignerFont): number {
  const flags = faceFlags(font);
  return (flags.bold ? 2 : 0) + (flags.italic ? 1 : 0);
}

/** Families used by the design right now — Zazzle's "Fonts in this design". */
export function familiesInUse(fonts: readonly DesignerFont[], fontIds: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const id of fontIds) {
    const font = fonts.find((item) => item.id === id);
    if (font) seen.add(font.family);
  }
  return [...seen];
}

// ── Stepper ──────────────────────────────────────────────────────────────────

export interface StepperOptions {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** Shown after the number ("mm", "%", "°"). */
  unit?: string;
  /** Digits after the decimal point in the field; defaults from `step`. */
  decimals?: number;
  /** Also draw a range slider under the field (desktop Effects panels do). */
  slider?: boolean;
  /** Fired while the slider moves (live preview); optional. */
  onInput?(value: number): void;
  /** Fired on −/+ press, Enter, blur and slider release. */
  onCommit(value: number): void;
}

export interface StepperHandle {
  element: HTMLElement;
  setValue(value: number): void;
  destroy(): void;
}

function decimalsFor(step: number, given?: number): number {
  if (given !== undefined) return given;
  if (step >= 1) return 0;
  if (step >= 0.1) return 1;
  return 2;
}

/**
 * Zazzle's `− [number] +` with the paired slider, and the one implementation
 * of it in this designer. The number field is authoritative: the buttons, the
 * wheel, the arrow keys and the slider all write into it, Enter and blur
 * commit, and anything that is not a number in range snaps back to the last
 * good value instead of committing a NaN the validator would have to explain.
 */
export function createStepper(options: StepperOptions): StepperHandle {
  const decimals = decimalsFor(options.step, options.decimals);
  const format = (value: number): string => value.toFixed(decimals);
  let value = clamp(options.value, options.min, options.max);

  const wrap = el('div', 'pzd-stepper');
  const head = el('div', 'pzd-field-head');
  head.append(el('span', 'pzd-lbl', options.label));
  wrap.append(head);

  const row = el('div', 'pzd-stepper-row');
  const minus = iconBtn('minus', `${options.label} −`, () => commit(value - options.step), { className: 'pzd-step-btn', size: 16 });
  const input = el('input', 'pzd-step-input');
  input.type = 'text';
  input.inputMode = 'decimal';
  input.value = format(value);
  input.setAttribute('aria-label', options.label);
  input.setAttribute('role', 'spinbutton');
  const plus = iconBtn('plus', `${options.label} +`, () => commit(value + options.step), { className: 'pzd-step-btn', size: 16 });
  row.append(minus, input);
  if (options.unit) row.append(el('span', 'pzd-step-unit', options.unit));
  row.append(plus);
  wrap.append(row);

  let range: HTMLInputElement | undefined;
  if (options.slider) {
    range = el('input', 'pzd-range');
    range.type = 'range';
    range.min = String(options.min);
    range.max = String(options.max);
    range.step = String(options.step);
    range.value = String(value);
    range.setAttribute('aria-label', options.label);
    wrap.append(range);
  }

  function paint(): void {
    input.value = format(value);
    input.setAttribute('aria-valuenow', String(value));
    input.setAttribute('aria-valuemin', String(options.min));
    input.setAttribute('aria-valuemax', String(options.max));
    if (range) range.value = String(value);
    minus.disabled = value <= options.min + 1e-9;
    plus.disabled = value >= options.max - 1e-9;
  }

  function commit(next: number): void {
    const clamped = round(clamp(next, options.min, options.max), 4);
    value = clamped;
    paint();
    options.onCommit(clamped);
  }

  function readField(): void {
    const parsed = Number(input.value.replace(',', '.'));
    if (!Number.isFinite(parsed)) {
      paint();
      return;
    }
    commit(parsed);
  }

  input.addEventListener('keydown', (event) => {
    const key = (event as KeyboardEvent).key;
    if (key === 'Enter') {
      event.preventDefault();
      readField();
      return;
    }
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      commit(value + (key === 'ArrowUp' ? options.step : -options.step));
    }
  });
  input.addEventListener('blur', readField);
  // The wheel adjusts only while the field has the focus, so scrolling a panel
  // past a stepper never changes a number the shopper was not looking at.
  const onWheel = (event: WheelEvent): void => {
    if (document.activeElement !== input && input.ownerDocument.activeElement !== input) return;
    event.preventDefault();
    commit(value + (event.deltaY < 0 ? options.step : -options.step));
  };
  input.addEventListener('wheel', onWheel, { passive: false });

  range?.addEventListener('input', () => {
    const next = clamp(Number(range!.value), options.min, options.max);
    value = next;
    input.value = format(next);
    options.onInput?.(next);
  });
  range?.addEventListener('change', () => commit(Number(range!.value)));

  paint();

  return {
    element: wrap,
    setValue(next) {
      value = clamp(next, options.min, options.max);
      paint();
    },
    destroy() {
      input.removeEventListener('wheel', onWheel);
      wrap.remove();
    },
  };
}

// ── Colour block ─────────────────────────────────────────────────────────────

export interface ColorBlockOptions {
  ctx: DesignerCtx;
  /** Section title ("Boja teksta"); omit for an untitled block. */
  title?: string;
  value: Color | undefined;
  /** The swatches offered first; defaults to `ctx.config.text.palette`. */
  palette?: Color[];
  /** Offer "Bez boje" as the first swatch (backgrounds, shape fills with a stroke). */
  allowNone?: boolean;
  /** Hex input + eyedropper; defaults to `ctx.config.text.allowCustomColor`. */
  allowCustom?: boolean;
  /** Live while a swatch is hovered/pressed, committed on pick. */
  onPick(color: Color | undefined): void;
}

export interface ColorBlockHandle {
  element: HTMLElement;
  /** Re-marks the selected swatch without rebuilding the block. */
  setValue(color: Color | undefined): void;
  destroy(): void;
}

/** How many swatches show before "Prikaži sve" — Zazzle collapses at 12. */
const COLLAPSED_SWATCHES = 12;

export function createColorBlock(options: ColorBlockOptions): ColorBlockHandle {
  const ctx = options.ctx;
  const texts = ctx.texts;
  const palette = options.palette?.length ? options.palette : ctx.config.text.palette;
  const allowCustom = options.allowCustom ?? ctx.config.text.allowCustomColor;
  let value = options.value;
  let expanded = false;
  let extraOpen = false;
  let extraTab: 'print' | 'web' = 'print';
  let stopPicking: (() => void) | undefined;

  const wrap = el('div', 'pzd-color-block');
  if (options.title) {
    const head = el('div', 'pzd-field-head');
    head.append(el('span', 'pzd-lbl', options.title));
    const readout = el('span', 'pzd-field-val');
    head.append(readout);
    wrap.append(head);
  }
  const custom = el('div', 'pzd-color-custom');
  const swatches = el('div', 'pzd-swatches');
  const moreBtn = el('button', 'pzd-more');
  moreBtn.type = 'button';
  const extra = el('div', 'pzd-color-extra');
  wrap.append(custom, el('span', 'pzd-lbl', texts.palettes), swatches, moreBtn, extra);

  function pick(next: Color | undefined): void {
    value = next;
    options.onPick(next);
    paint();
  }

  function swatchButton(color: Color): HTMLButtonElement {
    const hex = hexOf(color);
    const button = el('button', `pzd-swatch${sameHex(color, value) ? ' is-on' : ''}`);
    button.type = 'button';
    button.style.background = hex;
    button.setAttribute('aria-label', hex);
    button.title = hex;
    button.addEventListener('click', () => pick({ hex }));
    return button;
  }

  function paintSwatches(): void {
    swatches.textContent = '';
    if (options.allowNone) {
      const none = el('button', `pzd-swatch is-none${value ? '' : ' is-on'}`);
      none.type = 'button';
      none.setAttribute('aria-label', texts.noColor);
      none.title = texts.noColor;
      none.addEventListener('click', () => pick(undefined));
      swatches.append(none);
    }
    const shown = expanded ? palette : palette.slice(0, COLLAPSED_SWATCHES);
    for (const color of shown) {
      if (!hexOf(color)) continue;
      swatches.append(swatchButton(color));
    }
    moreBtn.hidden = palette.length <= COLLAPSED_SWATCHES;
    moreBtn.textContent = expanded ? texts.showFewerColors : texts.showAllColors;
  }

  function paintExtra(): void {
    extra.textContent = '';
    if (!allowCustom) return;
    const toggle = el('button', `pzd-disclose${extraOpen ? ' is-open' : ''}`);
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', String(extraOpen));
    toggle.innerHTML = iconSvg('chevron', 14);
    toggle.append(document.createTextNode(texts.additionalColors));
    toggle.addEventListener('click', () => {
      extraOpen = !extraOpen;
      paintExtra();
    });
    extra.append(toggle);
    if (!extraOpen) return;
    const tabRow = el('div', 'pzd-tabs');
    tabRow.setAttribute('role', 'tablist');
    for (const [id, label] of [['print', texts.printColors], ['web', texts.webColors]] as const) {
      const tab = el('button', extraTab === id ? 'is-on' : '');
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(extraTab === id));
      tab.textContent = label;
      tab.addEventListener('click', () => {
        extraTab = id;
        paintExtra();
      });
      tabRow.append(tab);
    }
    const grid = el('div', 'pzd-swatches is-dense');
    for (const color of extraTab === 'print' ? printPalette(ctx) : webPalette(ctx)) {
      if (!hexOf(color)) continue;
      grid.append(swatchButton(color));
    }
    extra.append(tabRow, grid);
  }

  function paintCustom(): void {
    custom.textContent = '';
    if (!allowCustom) return;
    custom.append(el('span', 'pzd-lbl', texts.customColorTitle));
    const row = el('div', 'pzd-color-custom-row');
    const dropper = iconBtn('magic', texts.eyedropper, () => startPicking(), { className: 'pzd-icon-btn is-drop' });
    const hex = el('input', 'pzd-input is-hex');
    hex.type = 'text';
    hex.spellcheck = false;
    hex.value = hexOf(value) || '';
    hex.placeholder = '#rrggbb';
    hex.setAttribute('aria-label', texts.hexLabel);
    const commitHex = (): void => {
      const parsed = parseHexInput(hex.value);
      if (!parsed) {
        hex.value = hexOf(value) || '';
        if (hex.value === '') hex.setAttribute('aria-invalid', 'true');
        return;
      }
      hex.removeAttribute('aria-invalid');
      pick({ hex: parsed });
    };
    hex.addEventListener('input', () => {
      const parsed = parseHexInput(hex.value);
      hex.classList.toggle('is-bad', hex.value.trim().length > 0 && !parsed);
    });
    hex.addEventListener('change', commitHex);
    hex.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key !== 'Enter') return;
      event.preventDefault();
      commitHex();
    });
    // The native picker stays available as the third way in: it is the only
    // one that works with a trackpad and no keyboard.
    const native = el('input', 'pzd-color-native');
    native.type = 'color';
    native.value = hexOf(value) || '#000000';
    native.setAttribute('aria-label', texts.customColorTitle);
    native.addEventListener('change', () => {
      const parsed = parseHexInput(native.value);
      if (parsed) pick({ hex: parsed });
    });
    const nativeWrap = el('button', 'pzd-swatch is-custom');
    nativeWrap.type = 'button';
    nativeWrap.setAttribute('aria-label', texts.customColorTitle);
    nativeWrap.title = texts.customColorTitle;
    nativeWrap.append(native);
    nativeWrap.addEventListener('click', () => native.click());
    row.append(dropper, hex, nativeWrap);
    custom.append(row);
  }

  function paint(): void {
    const readout = wrap.querySelector('.pzd-field-val');
    if (readout) readout.textContent = hexOf(value) || (options.allowNone ? texts.noColor : '');
    paintCustom();
    paintSwatches();
    paintExtra();
  }

  function startPicking(): void {
    stopPicking?.();
    void pickColorFromDesign(ctx, (stop) => {
      stopPicking = stop;
    }).then((hex) => {
      stopPicking = undefined;
      if (!hex) return;
      pick({ hex });
    });
  }

  moreBtn.addEventListener('click', () => {
    expanded = !expanded;
    paintSwatches();
  });

  paint();

  return {
    element: wrap,
    setValue(next) {
      value = next;
      paint();
    },
    destroy() {
      stopPicking?.();
      wrap.remove();
    },
  };
}

/**
 * "Izaberi boju iz dizajna".
 *
 * Chrome's own `EyeDropper` samples the composited screen, which is exactly
 * what a shopper means by "that pink", so it is used wherever it exists.
 * Everywhere else the designer samples its own rasters: the canvases it drew
 * are in the same shadow root, and reading one pixel out of the topmost one
 * under the pointer gives the same answer for every colour the designer itself
 * put on screen.
 */
export function pickColorFromDesign(
  ctx: DesignerCtx,
  onStart?: (cancel: () => void) => void,
): Promise<string | undefined> {
  const native = (window as unknown as { EyeDropper?: new () => { open(): Promise<{ sRGBHex: string }> } }).EyeDropper;
  if (native) {
    onStart?.(() => undefined);
    return new native().open()
      .then((result) => parseHexInput(result.sRGBHex))
      .catch(() => undefined);
  }
  return new Promise((resolve) => {
    ctx.root.classList.add('is-eyedropping');
    ctx.toast(ctx.texts.eyedropperHint, 'info');
    const finish = (hex: string | undefined): void => {
      ctx.root.classList.remove('is-eyedropping');
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      resolve(hex);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      finish(undefined);
    };
    const onDown = (event: PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      finish(sampleCanvases(ctx, event.clientX, event.clientY));
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    onStart?.(() => finish(undefined));
  });
}

/** The topmost designer canvas under the point, and the pixel it holds there. */
function sampleCanvases(ctx: DesignerCtx, clientX: number, clientY: number): string | undefined {
  const canvases = [...ctx.root.querySelectorAll<HTMLCanvasElement>('canvas')].reverse();
  for (const canvas of canvases) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) continue;
    const x = Math.floor(((clientX - rect.left) / rect.width) * canvas.width);
    const y = Math.floor(((clientY - rect.top) / rect.height) * canvas.height);
    try {
      const data = canvas.getContext('2d')?.getImageData(x, y, 1, 1).data;
      if (!data || data[3]! < 8) continue;
      return rgbToHex(data[0]!, data[1]!, data[2]!);
    } catch {
      // A canvas tainted by a cross-origin asset cannot be read; try the next.
      continue;
    }
  }
  ctx.toast(ctx.texts.eyedropperFailed, 'info');
  return undefined;
}

function rgbToHex(r: number, g: number, b: number): string {
  const part = (value: number): string => value.toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

// ── Font picker ──────────────────────────────────────────────────────────────

export interface FontPickerOptions {
  ctx: DesignerCtx;
  /** The face currently on the layer. */
  fontId: string;
  /** Where the picker card mounts (the panel slot on desktop, a sheet on a phone). */
  host: HTMLElement;
  onPick(fontId: string): void;
  onClose(): void;
}

export interface FontPickerHandle {
  element: HTMLElement;
  destroy(): void;
}

const CATEGORY_LABEL_KEYS: Record<DesignerFontCategory, keyof DesignerCtx['texts']> = {
  serif: 'catSerif',
  sans: 'catSans',
  script: 'catScript',
  handwriting: 'catHandwriting',
  display: 'catDisplay',
  decorative: 'catDecorative',
  mono: 'catMono',
  symbols: 'catSymbols',
};

/**
 * Zazzle's Fonts dialog: a search box, the category chips that are actually
 * present, the families this design already uses, and rows that show each
 * family's NAME set in that family — with a chevron for the families that have
 * more than one face.
 *
 * Faces load lazily through an IntersectionObserver: a shop with two hundred
 * fonts must not fetch two hundred files to show a list.
 */
export function openFontPicker(options: FontPickerOptions): FontPickerHandle {
  const ctx = options.ctx;
  const texts = ctx.texts;
  const fonts = ctx.fonts.list;
  const groups = groupFontFamilies(fonts);
  const inUse = new Set(familiesInUse(fonts, ctx.store.value().layers.map((layer) => (layer.kind === 'text' ? layer.fontId : ''))));
  const current = fonts.find((font) => font.id === options.fontId);

  // Local preference only; never part of the printed design or font ownership.
  const favoriteKey = 'personalizy:favorite-fonts:v1';
  let favorites = new Set<string>();
  try { const saved = JSON.parse(localStorage.getItem(favoriteKey) ?? '[]'); if (Array.isArray(saved)) favorites = new Set(saved.filter((x): x is string => typeof x === 'string' && x.length <= 200).slice(0, 200)); } catch { /* private browsing */ }
  let chip = 'popular';
  let query = '';
  const expandedFamilies = new Set<string>();

  const card = el('div', 'pzd-fontpicker');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', texts.fontsTitle);
  const head = el('div', 'pzd-over-head');
  head.append(el('h4', undefined, texts.fontsTitle));
  head.append(iconBtn('close', texts.close, () => options.onClose(), { className: 'pzd-icon-btn is-plain' }));
  const search = searchBox(texts.fontSearch, '', (next) => {
    query = next.toLocaleLowerCase('sr-RS');
    paintList();
  });
  const chips = el('div', 'pzd-tabs');
  const list = el('div', 'pzd-font-list');
  card.append(head, search, chips, list);
  options.host.append(card);

  const observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const node = entry.target as HTMLElement;
        const fontId = node.dataset.font;
        observer?.unobserve(node);
        if (!fontId) continue;
        void ctx.fonts.ensure(fontId).then(() => {
          node.style.fontFamily = `"${ctx.fonts.cssFamily(fontId)}", sans-serif`;
        }).catch(() => undefined);
      }
    }, { root: list, rootMargin: '160px' })
    : undefined;

  function categoriesPresent(): DesignerFontCategory[] {
    const present = new Set(groups.map((group) => group.category));
    return FONT_CATEGORIES.filter((category) => present.has(category));
  }

  function paintChips(): void {
    chips.textContent = '';
    const items: Array<{ id: string; label: string }> = [
      { id: 'popular', label: texts.fontsPopular },
      { id: 'all', label: texts.fontsAll },
      { id: 'favorites', label: 'Omiljeni' },
      ...categoriesPresent().map((category) => ({
        id: category,
        label: String(texts[CATEGORY_LABEL_KEYS[category]]),
      })),
    ];
    if (groups.some((group) => group.cyrillic)) items.push({ id: 'cyrillic', label: texts.catCyrillic });
    for (const item of items) {
      const button = el('button', chip === item.id ? 'is-on' : '');
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(chip === item.id));
      button.textContent = item.label;
      button.addEventListener('click', () => {
        chip = item.id;
        paintChips();
        paintList();
      });
      chips.append(button);
    }
  }

  function matching(): FontFamilyGroup[] {
    const needle = query.trim();
    let shown = groups;
    if (chip === 'favorites') shown = shown.filter(group => favorites.has(group.family));
    else if (chip === 'cyrillic') shown = shown.filter((group) => group.cyrillic);
    else if (chip !== 'all' && chip !== 'popular') shown = shown.filter((group) => group.category === chip);
    // "Popularni" is the shop's own order — the list arrives ranked — capped
    // so the first screen is a choice rather than an inventory.
    else if (chip === 'popular' && !needle) shown = shown.slice(0, 40);
    if (!needle) return shown;
    return shown.filter((group) => group.family.toLocaleLowerCase('sr-RS').includes(needle));
  }

  function familyRow(group: FontFamilyGroup): HTMLElement {
    const row = el('div', 'pzd-font-row');
    const face = group.faces[0]!;
    const pickBtn = el('button', `pzd-font-name${group.faces.some((item) => item.id === options.fontId) ? ' is-on' : ''}`);
    pickBtn.type = 'button';
    pickBtn.textContent = group.family;
    pickBtn.dataset.font = face.id;
    pickBtn.setAttribute('aria-label', group.family);
    pickBtn.addEventListener('click', () => options.onPick(keepStyle(group, options.fontId) ?? face.id));
    const favorite = el('button', 'pzd-font-more'); favorite.type = 'button'; favorite.dataset.favoriteFamily = group.family;
    const favoriteState = () => { const on = favorites.has(group.family); favorite.textContent = on ? '★' : '☆'; favorite.setAttribute('aria-pressed', String(on)); favorite.setAttribute('aria-label', (on ? 'Ukloni iz omiljenih: ' : 'Dodaj u omiljene: ') + group.family); };
    favoriteState();
    favorite.addEventListener('click', () => {
      if (favorites.has(group.family)) favorites.delete(group.family); else if (favorites.size < 200) favorites.add(group.family);
      try { localStorage.setItem(favoriteKey, JSON.stringify([...favorites])); } catch { /* session-only fallback */ }
      for (const button of list.querySelectorAll<HTMLButtonElement>('[data-favorite-family]')) {
        if (button.dataset.favoriteFamily !== group.family) continue;
        const on = favorites.has(group.family); button.textContent = on ? '★' : '☆'; button.setAttribute('aria-pressed', String(on)); button.setAttribute('aria-label', (on ? 'Ukloni iz omiljenih: ' : 'Dodaj u omiljene: ') + group.family);
      }
      if (chip === 'favorites') paintList();
    });
    row.append(pickBtn, favorite);
    if (observer) observer.observe(pickBtn);
    else {
      void ctx.fonts.ensure(face.id).then(() => {
        pickBtn.style.fontFamily = `"${ctx.fonts.cssFamily(face.id)}", sans-serif`;
      }).catch(() => undefined);
    }
    if (group.faces.length > 1) {
      const open = expandedFamilies.has(group.family);
      const chevron = el('button', `pzd-font-more${open ? ' is-open' : ''}`);
      chevron.type = 'button';
      chevron.innerHTML = iconSvg('chevron', 16);
      chevron.setAttribute('aria-label', texts.fontVariants);
      chevron.setAttribute('aria-expanded', String(open));
      chevron.addEventListener('click', () => {
        if (open) expandedFamilies.delete(group.family);
        else expandedFamilies.add(group.family);
        paintList();
      });
      row.append(chevron);
    }
    return row;
  }

  function variantRows(group: FontFamilyGroup): HTMLElement {
    const box = el('div', 'pzd-font-variants');
    for (const face of group.faces) {
      const button = el('button', `pzd-font-variant${face.id === options.fontId ? ' is-on' : ''}`);
      button.type = 'button';
      button.textContent = face.style || group.family;
      button.addEventListener('click', () => options.onPick(face.id));
      box.append(button);
    }
    return box;
  }

  function paintList(): void {
    list.textContent = '';
    const shown = matching();
    if (inUse.size && !query && chip === 'popular') {
      const used = groups.filter((group) => inUse.has(group.family));
      if (used.length) {
        list.append(el('div', 'pzd-lbl', texts.fontsInDesign));
        for (const group of used) list.append(familyRow(group));
      }
    }
    if (!shown.length) {
      list.append(el('div', 'pzd-hint', texts.noResults));
      return;
    }
    list.append(el('div', 'pzd-lbl', chip === 'favorites' ? 'Omiljeni' : chip === 'popular' ? texts.fontsPopular : texts.fontsAll));
    for (const group of shown) {
      list.append(familyRow(group));
      if (expandedFamilies.has(group.family)) list.append(variantRows(group));
    }
  }

  /** Switching family keeps the Bold/Italic the shopper already chose. */
  function keepStyle(group: FontFamilyGroup, fromId: string): string | undefined {
    const from = fonts.find((font) => font.id === fromId);
    if (!from) return undefined;
    const wanted = faceFlags(from);
    const exact = group.faces.find((face) => {
      const flags = faceFlags(face);
      return flags.bold === wanted.bold && flags.italic === wanted.italic;
    });
    return exact?.id;
  }

  paintChips();
  paintList();
  if (current) void ctx.fonts.ensure(current.id).catch(() => undefined);

  return {
    element: card,
    destroy() {
      observer?.disconnect();
      card.remove();
    },
  };
}
